import xml, { Element as XmlElement } from '@xmpp/xml';
import { isNil } from 'lodash-es';

import { I18nExtensionType } from './types';

export type MessageType = 'composing' | 'paused' | 'stamp' | 'text';
export type Message<T> = T extends 'composing' | 'paused' ? null : T extends 'stamp' ? Thumb : string;
export type Thumb = { bundleId: string, stampId: string, url: string };

function initializeMessage(senderName: string, to: string, isGroup: boolean) {
  const type = isGroup ? 'groupchat' : 'chat';
  return xml(
    'message',
    { to, type, xmlns: 'jabber:client' },
    xml('nick', 'http://jabber.org/protocol/nick', senderName || ''),
  );
}

function buildI18nTemplateArgs(senderName: string, message?: string) {
  const nodes = [xml('templateArg', {}, senderName)];
  if (!isNil(message)) {
    nodes.push(xml('templateArg', {}, message));
  }
  return nodes;
}

function buildI18nExtras(extras: Record<string, string> = {}) {
  const entries = Object.entries(extras);
  return entries.map(([key, value]) => xml('extra', { inpush: 'true', key }, value));
}

function buildI18nExtension(
  xmlMessage: XmlElement,
  type: I18nExtensionType,
  senderName: string,
  { groupName, message, extras = {} }: { groupName: string | null, message?: string, extras?: Record<string, string> },
) {
  if (groupName) {
    extras = {
      // Add target group's name. Send groupname so that older iOS clients don't break.
      ...extras,
      group_name: groupName,
      groupname: groupName,
    };
  }

  const i18nNode = xml(
    'i18n',
    { notification: 'normal', xmlns: 'xmpp:join:i18n' },
    xml('templateName', {}, type),
    ...buildI18nTemplateArgs(senderName || '', message),
    ...buildI18nExtras(extras),
  );

  xmlMessage.append(i18nNode);
  return xmlMessage;
}

function buildTextMessage(xmlMessage: XmlElement, message: string, senderName: string, groupName: string | null) {
  buildI18nExtension(xmlMessage, I18nExtensionType.TEXT, senderName, { groupName, message });
  xmlMessage.append(
    xml('markdown'),
    xml('body', {}, message),
    xml('active', 'xmpp:join'),
  );
  return xmlMessage;
}

export function buildSendMessage(message: Message<MessageType>, type: MessageType, senderName: string, to: string, groupName: string | null) {
  if (message === '') return null;

  const xmlMessage = initializeMessage(senderName, to, !!groupName);
  buildTextMessage(xmlMessage, message as string, senderName, groupName);

  return xmlMessage;
}
